import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { PageHeader, Btn, SearchInput, Toolbar, EmptyState, Spinner } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Trash2, Upload, Download } from "lucide-react";
import { fmtDate } from "@/lib/format";

const EMPTY = { name: "", company: "", email: "", phone: "", address: "", notes: "", tags: [] };

export default function Customers() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { data, loading, refetch } = useFetch(`/customers?search=${encodeURIComponent(search)}`, [search]);

  useEffect(() => { if (params.get("new")) { setOpen(true); params.delete("new"); setParams(params, { replace: true }); } }, []); // eslint-disable-line

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false); setForm(EMPTY); refetch();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this customer?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); refetch(); }
    catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const items = data?.items || [];
  const fileRef = useRef();
  const doExport = async () => {
    const res = await api.get("/customers/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "customers.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data: r } = await api.post("/customers/import", fd); toast.success(`Imported ${r.imported} customers`); refetch(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    e.target.value = "";
  };
  return (
    <div className="animate-fade-up">
      <PageHeader title="Customer Hub" description="Everyone you do business with, in one place."
        action={<>
          <input ref={fileRef} type="file" accept=".csv" onChange={doImport} className="hidden" />
          <Btn variant="secondary" onClick={() => fileRef.current?.click()} data-testid="import-customers-button"><Upload size={16} /> Import</Btn>
          <Btn variant="secondary" onClick={doExport} data-testid="export-customers-button"><Download size={16} /> Export</Btn>
          <Btn variant="accent" onClick={() => setOpen(true)} data-testid="create-customer-button"><Plus size={16} /> New customer</Btn>
        </>} />
      <Toolbar>
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers…" testId="customer-search-input" />
        <span className="text-sm text-slate-400 ml-auto">{data?.total || 0} customers</span>
      </Toolbar>

      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={Users} title="No customers yet" description="Add your first customer to start creating quotes and invoices."
          action={<Btn variant="accent" onClick={() => setOpen(true)}><Plus size={16} /> New customer</Btn>} />
      ) : (
        <div className="amden-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold hidden sm:table-cell">Company</th>
              <th className="px-4 py-3 font-semibold hidden md:table-cell">Email</th>
              <th className="px-4 py-3 font-semibold hidden md:table-cell">Phone</th>
              <th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} onClick={() => nav(`/app/customers/${c.id}`)} data-testid={`customer-row-${c.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50/80 cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-medium text-navy">{c.name}
                    {c.tags?.map((t) => <span key={t} className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-accent">{t}</span>)}
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{c.company || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{c.email || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{c.phone || "—"}</td>
                  <td className="px-4 py-3 text-right"><button onClick={(e) => remove(c.id, e)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50"><Trash2 size={16} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            {[["Name *", "name"], ["Company", "company"], ["Email", "email"], ["Phone", "phone"], ["Address", "address"]].map(([l, k]) => (
              <div key={k}>
                <label className="text-sm font-medium text-slate-700">{l}</label>
                <input value={form[k]} onChange={set(k)} data-testid={`customer-field-${k}`} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn variant="accent" onClick={save} disabled={saving} data-testid="customer-save-button">{saving ? "Saving…" : "Save customer"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
